import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import ReactECharts from 'echarts-for-react';
import {
  STAGES, money, parseMoney, range, stageTicket, ticketBounds, wizardLabel, geoMean, snapStep,
} from '../../data/fundingStages';
import type { Stage } from '../../data/fundingStages';
import { useIsDark } from '../../useIsDark';

/**
 * FundingStagesExplorer — interactive funding-stages figure (D-058 / RULES_BLOG_POST Rule 42).
 *
 * The selection happens ON the figure, not in a side panel:
 *   • Funnel (top)            — the journey; click a stage to pick the round you're planning.
 *   • Your Round scrubber     — set your round size. Log scale, NO limits.
 *   • Your ticket size scrubber — set your ticket. Connected to the round (ticket ≈ round ÷ ~4
 *     investors) until you move it yourself; then it's fully independent.
 *   • The shaded band on each scrubber is the "typical" guide for the selected round — the
 *     handle can go ANYWHERE (a $50k round, a $10M pre-seed — all fine). Guide, never restrict.
 *   • Full mapping table below — the structured, SEO-visible record (Rule 42).
 * One shared formatter (`money`/`range`/`parseMoney`) is used everywhere (Rule 21 — labels
 * can never drift). No tooltips (redundant — the value is on the control).
 */

type ColorSet = { on: string; off: string; bg: string; bd: string };

/** Accent palette per tone. The blog default is cyan; the CRM embeds the same figure in
 * brand teal (BRAND_COLORS.teal) so the funnel matches the rest of the product. */
type ToneKey = 'cyan' | 'teal';

type Tone = ColorSet & {
  band: string; handle: string; hint: string; box: string; pill: string;
  dot: string; relink: string; activeRow: string; activeName: string; cta: string;
};

const TONES: Record<ToneKey, Tone> = {
  cyan: {
    on: '#0e7490', off: '#06b6d4', bg: '#ffffff', bd: '',
    band: 'bg-cyan-200/80 dark:bg-cyan-700/40',
    handle: 'bg-cyan-600',
    hint: 'text-cyan-600 dark:text-cyan-400',
    box: 'border-cyan-200 bg-cyan-50/40 dark:border-cyan-900 dark:bg-cyan-950/20',
    pill: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',
    dot: 'bg-cyan-500',
    relink: 'border-cyan-500/50 text-cyan-600 hover:bg-cyan-50 dark:text-cyan-400 dark:hover:bg-cyan-950/40',
    activeRow: 'bg-cyan-50 ring-1 ring-inset ring-cyan-400/50 dark:bg-cyan-950/30',
    activeName: 'text-cyan-700 dark:text-cyan-300',
    cta: 'bg-cyan-600 hover:bg-cyan-700',
  },
  teal: {
    on: '#0AB3BF', off: '#4DD4E0', bg: '#ffffff', bd: '',
    band: 'bg-teal-200/80 dark:bg-teal-700/40',
    handle: 'bg-teal-500',
    hint: 'text-teal-600 dark:text-teal-400',
    box: 'border-teal-200 bg-teal-50/40 dark:border-teal-900 dark:bg-teal-950/20',
    pill: 'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300',
    dot: 'bg-teal-500',
    relink: 'border-teal-500/50 text-teal-600 hover:bg-teal-50 dark:text-teal-400 dark:hover:bg-teal-950/40',
    activeRow: 'bg-teal-50 ring-1 ring-inset ring-teal-400/50 dark:bg-teal-950/30',
    activeName: 'text-teal-700 dark:text-teal-300',
    cta: 'bg-teal-500 hover:bg-teal-600',
  },
};

const toneColor = (tone: ToneKey, dark: boolean): ColorSet =>
  dark
    ? { on: tone === 'teal' ? '#4DD4E0' : '#22d3ee', off: tone === 'teal' ? '#0D8A93' : '#155e75', bg: '#0f172a', bd: 'border-slate-200 dark:border-slate-700' }
    : { ...TONES[tone], bd: 'border-slate-200 dark:border-slate-700' };

// Scrubber track bounds (log scale). The handle can go ANYWHERE within these ranges —
// and the free-text input accepts values beyond them too. Nothing clamps the user.
const ROUND_MIN = 10_000;        // $10k
const ROUND_MAX = 200_000_000;   // $200M
const TICKET_MIN = 1_000;        // $1k
const TICKET_MAX = 100_000_000;  // $100M
// Connection: the ticket SUGGESTS round ÷ this many investors. A suggestion, not a clamp.
const INVESTORS_DIVISOR = 4;

// Which round does a raise amount fall into (containment, else nearest by centre)?
// Used so dragging the round/ticket scrubbers ALSO drives the funnel highlight and the
// typical bands — the guide follows your value, but never resets it.
const roundForValue = (v: number): Stage => {
  const inside = STAGES.find((s) => v >= s.raiseMin && v <= s.raiseMax);
  if (inside) return inside;
  return STAGES.reduce((best, s) => {
    const db = Math.abs(v - (best.raiseMin + best.raiseMax) / 2);
    const ds = Math.abs(v - (s.raiseMin + s.raiseMax) / 2);
    return ds < db ? s : best;
  }, STAGES[0]);
};
const roundForTicket = (v: number): Stage => {
  const inside = STAGES.find((s) => {
    const t = ticketBounds(s);
    return v >= t.min && v <= t.max;
  });
  if (inside) return inside;
  return STAGES.reduce((best, s) => {
    const tb = ticketBounds(best);
    const ts = ticketBounds(s);
    const db = Math.abs(v - (tb.min + tb.max) / 2);
    const ds = Math.abs(v - (ts.min + ts.max) / 2);
    return ds < db ? s : best;
  }, STAGES[0]);
};

/** A log-scale value scrubber with a shaded "typical" band (the guide). The handle is free —
 * dragging anywhere on the track sets a value, and the free-text input accepts any amount. */
function Scrubber({
  label, value, onChange, typicalMin, typicalMax, min, max, hint,
  band, handle, hintColor, prominent = false,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  typicalMin: number;
  typicalMax: number;
  min: number;
  max: number;
  hint: string;
  band: string;
  handle: string;
  hintColor: string;
  /** prominent (CRM minimal): bigger label + a much larger value figure. */
  prominent?: boolean;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef<boolean>(false);
  const [text, setText] = useState<string>(money(value));

  useEffect(() => { setText(money(value)); }, [value]);

  const LOG_MIN = Math.log10(min);
  const LOG_MAX = Math.log10(max);
  const pos = (v: number) => {
    const frac = (Math.log10(Math.max(v, min)) - LOG_MIN) / (LOG_MAX - LOG_MIN);
    return Math.min(Math.max(frac * 100, 0), 100);
  };

  const setFromClientX = (clientX: number) => {
    const el = trackRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const frac = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
    onChange(snapStep(Math.round(Math.pow(10, LOG_MIN + frac * (LOG_MAX - LOG_MIN)))));
  };

  const nudge = (dir: 1 | -1) =>
    onChange(snapStep(Math.round(Math.pow(10, Math.log10(Math.max(value, min)) + dir * 0.1))));

  return (
    <div className="mt-4">
      <div className="flex items-center justify-between gap-2">
        <label className={`font-semibold uppercase tracking-wide ${prominent ? 'text-label text-slate-700 dark:text-slate-300' : 'text-label-xs text-slate-600 dark:text-slate-500'}`}>{label}</label>
        <div className="flex items-center gap-2">
          {prominent ? (
            <span className="rounded-md bg-[#0AB3BF] px-2 py-0.5 text-2xl font-bold text-white">{money(value)}</span>
          ) : (
            <span className="text-sm font-bold text-slate-900 dark:text-white">{money(value)}</span>
          )}
          {value > max && (
            <span className="rounded bg-amber-100 px-1.5 py-0.5 text-label-2xs font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
              above range
            </span>
          )}
          <input
            className="w-24 rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              const n = parseMoney(e.target.value);
              if (n != null) onChange(snapStep(n));
            }}
            onBlur={() => setText(money(value))}
            aria-label={`${label} exact value`}
          />
        </div>
      </div>

      <div
        ref={trackRef}
        role="slider"
        tabIndex={0}
        aria-label={label}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={value}
        className="relative mt-2 h-7 w-full cursor-pointer touch-none select-none rounded-full bg-slate-100 dark:bg-slate-800"
        onPointerDown={(e) => {
          draggingRef.current = true;
          setFromClientX(e.clientX);
          e.currentTarget.setPointerCapture?.(e.pointerId);
        }}
        onPointerMove={(e) => { if (draggingRef.current) setFromClientX(e.clientX); }}
        onPointerUp={() => { draggingRef.current = false; }}
        onPointerLeave={() => { draggingRef.current = false; }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowUp') nudge(1);
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') nudge(-1);
        }}
      >
        {/* typical band — the guide for the selected round */}
        <div
          className={`absolute top-1/2 h-2.5 -translate-y-1/2 rounded-full ${band}`}
          style={{ left: `${pos(typicalMin)}%`, width: `${Math.max(pos(typicalMax) - pos(typicalMin), 0.5)}%` }}
        />
        {/* the user's value handle — goes anywhere */}
        <div
          className={`absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white ${handle} shadow`}
          style={{ left: `${pos(value)}%` }}
        />
      </div>

      <div className="mt-1 flex items-center justify-between text-label-2xs text-slate-600 dark:text-slate-500">
        <span>{money(min)}</span>
        <span className={`font-medium ${hintColor}`}>{hint}</span>
        <span>{money(max)}</span>
      </div>
    </div>
  );
}

export const FundingStagesExplorer = ({
  dark,
  breakout = true,
  compact = false,
  onProfileChange,
  initialProfile,
  tone = 'cyan',
  title,
  minimal = false,
}: {
  /** Theme override; when omitted the figure follows the live html class theme. */
  dark?: boolean;
  /** Blog default: pull the figure out of the prose column (negative margins). */
  breakout?: boolean;
  /** Compact (landing/entity wizard): hide the CTA + SEO table, keep funnel + scrubbers. */
  compact?: boolean;
  /** Report the profile (selected round name + raise amount + ticket) to a parent. In
   *  minimal (CRM) mode `declared` says which decisions the user has actively committed
   *  (clicked/dragged) vs merely suggested. */
  onProfileChange?: (p: {
    round: string; raisingAmount: number; checkSize: number;
    declared: { round: boolean; amount: boolean; ticket: boolean };
  }) => void;
  /** Optional starting profile to seed the figure (e.g. an entity's known round + raise). */
  initialProfile?: { round: string; raisingAmount?: number; checkSize?: number };
  /** Accent palette: blog cyan (default) or brand teal for the CRM wizard. */
  tone?: ToneKey;
  /** Override the figure heading (CRM wizard gets a short one, blog keeps the long SEO one). */
  title?: string;
  /** Minimal (CRM): shrink the funnel + trim the guide copy so the figure reads as one
   *  compact "choose your round" control, not a full explainer. */
  minimal?: boolean;
}) => {
  // Blog/landing seed the figure from an entity benchmark (or default to Pre-seed).
  // Minimal (CRM) starts UNDECLARED: nothing is highlighted and no profile is reported
  // until the user actively clicks a stage. Every decision must be made, not pre-logged.
  const initialStage = STAGES.find((s) => s.round === initialProfile?.round) ?? STAGES[0];
  const liveDark = useIsDark();
  const isDark = dark ?? liveDark;
  const [selected, setSelected] = useState<number | null>(minimal ? null : initialStage.order);
  const [hovered, setHovered] = useState<number | null>(null);
  // Which of Round / Amount / Ticket the user has actively DECLARED (clicked/dragged).
  // Clicking a round only SUGGESTS its amount + ticket — they stay undeclared until the user
  // sets them, and clicking a different round resets both (their values changed automatically).
  const [declared, setDeclared] = useState<{ round: boolean; amount: boolean; ticket: boolean }>({
    round: !minimal, amount: false, ticket: false,
  });
  // Non-minimal: the round your current values fall into (gentle soft highlight).
  const [valueRound, setValueRound] = useState<number>(initialStage.order);
  // Default the ball to the LOG middle (geometric mean) of the typical range, snapped to a
  // clean $5k step, so it sits centred in the shaded band.
  const [round, setRound] = useState<number>(() =>
    minimal ? 0 : (initialProfile?.raisingAmount ?? snapStep(geoMean(STAGES[0].raiseMin, STAGES[0].raiseMax))),
  );
  const [ticket, setTicket] = useState<number>(() => {
    if (minimal) return 0;
    if (initialProfile?.checkSize) return snapStep(initialProfile.checkSize);
    const tb = ticketBounds(initialStage);
    return snapStep(geoMean(tb.min, tb.max));
  });
  // Connected by default (non-minimal): dragging the amount moves the ticket suggestion
  // (round ÷ ~N investors). Minimal keeps the two scrubbers independent — each its own decision.
  const [linked, setLinked] = useState<boolean>(!minimal);

  const hasRound = selected != null;
  const active = hasRound ? (STAGES.find((s) => s.order === selected) ?? STAGES[0]) : STAGES[0];
  const C = toneColor(tone, isDark);
  const T = TONES[tone];
  // Minimal anchors the guide to the DECLARED round. Non-minimal keeps the follow-the-value
  // content (bands/investors follow the round your amounts fall into).
  const guideStage = minimal ? active : (STAGES.find((s) => s.order === valueRound) ?? active);
  const guideTb = ticketBounds(guideStage);

  // Pick a round (funnel click or table row click) → DECLARE the round and re-anchor both
  // scrubbers to the LOG middle of its typical ranges ($5k-snapped). Those two values are only
  // SUGGESTIONS — amount and ticket still need to be set by the user before they count.
  const pickRound = (order?: number) => {
    if (!order) return;
    const s = STAGES.find((x) => x.order === order) ?? active;
    const st = ticketBounds(s);
    setSelected(order);
    setValueRound(order);
    setRound(snapStep(geoMean(s.raiseMin, s.raiseMax)));
    setTicket(snapStep(geoMean(st.min, st.max)));
    setLinked(!minimal);
    if (minimal) setDeclared({ round: true, amount: false, ticket: false });
  };

  // Amount scrubber — a real user action declares decision 2 (amount).
  const onRoundChange = (v: number) => {
    setRound(v);
    if (minimal) {
      setDeclared((d) => ({ ...d, amount: true }));
      return;
    }
    if (linked) setTicket(snapStep(Math.round(v / INVESTORS_DIVISOR)));
    setValueRound(roundForValue(v).order);
  };
  // Ticket scrubber — a real user action declares decision 3 (ticket).
  const onTicketChange = (v: number) => {
    setTicket(v);
    if (minimal) {
      setDeclared((d) => ({ ...d, ticket: true }));
      return;
    }
    setLinked(false);
    setValueRound(roundForTicket(v).order);
  };
  const relink = () => { if (!minimal) setLinked(true); setTicket(snapStep(Math.round(round / INVESTORS_DIVISOR))); };

  // Report the profile (and, in minimal, which decisions the user has declared) whenever it
  // changes. In minimal, nothing is reported until a round has actually been chosen.
  useEffect(() => {
    if (minimal && !hasRound) return;
    onProfileChange?.({ round: active.round, raisingAmount: round, checkSize: ticket, declared });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round, ticket, selected, declared.round, declared.amount, declared.ticket]);

  const funnelOption = useMemo(
    () => ({
      series: [
        {
          type: 'funnel' as const,
          sort: 'none', // keep stage order: pre-seed top, growth bottom
          left: '6%', right: '6%', top: 10, bottom: 10,
          minSize: '30%', maxSize: '100%', gap: 2,
          label: { show: true, position: 'inside', fontSize: 12, fontWeight: 600, formatter: '{b}' },
          data: STAGES.map((s) => ({
            name: s.round,
            value: Math.log10(s.raiseMax), // width ≈ typical round size (log) — honest, data-driven
            ...s,
            // Stage names: white only where the fill is dark enough to carry it —
            // the selected segment in light mode, everything in dark mode. On the
            // pale low-opacity fills of the light palette, dark text stays readable.
            label: { color: s.order === selected || isDark ? '#ffffff' : '#0f172a' },
            itemStyle: {
              // SELECTION (the round you clicked/committed) = solid fill + an ORANGE ring —
              // the one loud, valid state. Minimal (CRM) has no value-follow: only the declared
              // round stands out, hover is a light lift, everything else is dimmed.
              color: s.order === selected ? C.on : C.off,
              opacity:
                s.order === selected ? 1
                  : (!minimal && s.order === valueRound) ? 0.75
                  : s.order === hovered ? 0.5
                  : 0.28,
              borderWidth: s.order === selected ? 3 : 1,
              borderColor: s.order === selected ? (isDark ? '#fb923c' : '#ea580c') : C.bg,
            },
          })),
        },
      ],
    }),
    [selected, hovered, valueRound, minimal, C, isDark],
  );

  const funnelEvents = {
    mouseover: (p: { data?: Stage }) => setHovered(p?.data?.order ?? null),
    mouseout: () => setHovered(null),
    click: (p: { data?: Stage }) => pickRound(p?.data?.order),
  };

  return (
    <section className={`my-6 rounded-xl border ${C.bd} bg-white dark:bg-slate-900 ${minimal ? 'p-3 sm:p-4' : 'p-4 sm:p-6'}${breakout ? ' lg:-mx-16 xl:-mx-24' : ''}`}>
      {(title || !minimal) && (
        <h3 className={`font-semibold text-slate-900 dark:text-slate-100 ${minimal ? 'text-base' : 'text-lg'}`}>
          {title ?? 'Startup funding stages — pick your round, pick your ticket'}
        </h3>
      )}
      {!minimal && !title && (
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Click a stage in the funnel to pick the round you&apos;re planning, then drag the two handles
          below. The shaded band shows what&apos;s typical; the soft highlight marks the round your
          amounts currently fall into — your chosen round keeps the orange ring and its name.
        </p>
      )}

      {/* Funnel — pick the round */}
      <ReactECharts option={funnelOption} onEvents={funnelEvents} style={{ height: minimal ? 150 : 240 }} opts={{ renderer: 'canvas' }} />

      {/* CTA — start the raise, right between the pyramid and the profile */}
      {!compact && (
        <div className="mt-5">
          <Link
            to="/blog/the-raisy-beta-is-open"
            className={`block w-full rounded-lg ${T.cta} px-6 py-3.5 text-center text-sm font-semibold text-white shadow-sm transition-colors`}
          >
            Start preparing your raise →
          </Link>
          <p className="mt-1.5 text-center text-label-xs text-slate-600 dark:text-slate-500">
            Raisy turns this profile into a stage-matched investor list and drafts your outreach.
          </p>
        </div>
      )}

      {/* Round → amount → ticket: in minimal (CRM) each is its own declaration. */}
      <div className={`mt-4 rounded-xl border ${T.box} p-4 sm:p-5`}>
        {minimal && !hasRound ? (
          <p className="text-label leading-relaxed text-slate-500 dark:text-slate-400">
            <span className="font-semibold text-slate-700 dark:text-slate-200">Pick a round above</span>
            — then set your amount and ticket here. Each counts once you set it.
          </p>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {!minimal && (
                <h4 className="text-sm font-semibold text-slate-900 dark:text-white">Your raise profile</h4>
              )}
              <span className={`rounded-full ${T.pill} px-2.5 py-0.5 text-label-xs font-medium`}>
                {active.round}
              </span>
            </div>
            {!minimal && active.whatThisMeans && (
              <p className="mt-1 text-xs leading-relaxed text-slate-600 dark:text-slate-400">{active.whatThisMeans}</p>
            )}

            <Scrubber
              prominent={minimal}
              label={minimal ? 'Raise amount' : 'Your Round'}
              value={round}
              onChange={onRoundChange}
              typicalMin={guideStage.raiseMin}
              typicalMax={guideStage.raiseMax}
              min={ROUND_MIN}
              max={ROUND_MAX}
              hint={`typical ${range(guideStage.raiseMin, guideStage.raiseMax, guideStage.raiseOpen)}`}
              band={T.band}
              handle={T.handle}
              hintColor={T.hint}
            />
            <Scrubber
              prominent={minimal}
              label={minimal ? 'Ticket size' : 'Your ticket size'}
              value={ticket}
              onChange={onTicketChange}
              typicalMin={guideTb.min}
              typicalMax={guideTb.max}
              min={TICKET_MIN}
              max={TICKET_MAX}
              hint={`typical ${range(guideTb.min, guideTb.max)}`}
              band={T.band}
              handle={T.handle}
              hintColor={T.hint}
            />

            {!minimal && (linked ? (
              <p className="mt-3 flex items-center gap-1.5 text-label-xs leading-relaxed text-slate-600 dark:text-slate-500">
                <span className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${T.dot}`} />
                Connected — your ticket follows your round (≈ round ÷ {INVESTORS_DIVISOR} investors).
                Drag the ticket to set your own value.
              </p>
            ) : (
              <div className="mt-3 flex items-center justify-between gap-3">
                <p className="text-label-xs leading-relaxed text-slate-600 dark:text-slate-500">
                  Your ticket is a custom value and no longer follows the round.
                </p>
                <button
                  type="button"
                  onClick={relink}
                  className={`shrink-0 rounded-md border ${T.relink} px-2.5 py-1 text-label-xs font-semibold transition-colors`}
                >
                  Re-link to round
                </button>
              </div>
            ))}
            {!minimal && valueRound !== selected && (
              <p className={`mt-2 text-label-xs ${T.hint}`}>
                Your amounts fall in the <b>{STAGES.find((s) => s.order === valueRound)?.round}</b> range
                (softly highlighted) — your chosen round stays <b>{active.round}</b>.
              </p>
            )}
            {!minimal && (
              <p className="mt-2 text-label-xs leading-relaxed text-slate-600 dark:text-slate-500">
                No limits — drag within the track, or type any amount in the box to go beyond it
                (a $50k round, a $10M pre-seed, a $500M round…). The shaded band is only the guide.
              </p>
            )}

            {!minimal && (
              <>
                <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-label-xs text-slate-600 dark:text-slate-400">
                  <span className="font-medium uppercase tracking-wide text-slate-600 dark:text-slate-500">Investors:</span>
                  {guideStage.investors.map((inv) => (
                    <span key={inv.type}>
                      {inv.type} · {range(inv.ticketMin, inv.ticketMax, inv.open)}
                    </span>
                  ))}
                </div>
                <p className="mt-1 text-label-xs text-slate-600 dark:text-slate-400">
                  <span className="font-medium uppercase tracking-wide text-slate-600 dark:text-slate-500">Must show: </span>
                  {guideStage.maturityGate}
                </p>
              </>
            )}
          </>
        )}
      </div>

      {!minimal && (
        <p className={`mt-3 text-center text-xs px-4 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
          {active.round}: typical raise {range(active.raiseMin, active.raiseMax, active.raiseOpen)} · typical
          ticket {stageTicket(active)}. Ranges are indicative and vary by region and industry.
        </p>
      )}

      {/* Full mapping table — the structured, SEO-visible record (Rule 42) */}
      {!compact && (
        <div className="mt-5 overflow-x-auto">
        <table className="w-full min-w-[820px] border-collapse text-left text-sm">
          <thead>
            <tr className="border-b border-slate-300 text-label-xs uppercase tracking-wider text-slate-600 dark:border-slate-600 dark:text-slate-500">
              <th className="py-2 pr-3 font-bold">Round</th>
              <th className="py-2 pr-3 font-bold">Entity stage</th>
              <th className="py-2 pr-3 font-bold">Typical raise (min–max)</th>
              <th className="py-2 pr-3 font-bold">Typical ticket (min–max)</th>
              <th className="py-2 pr-3 font-bold">Investors to target</th>
              <th className="py-2 font-bold">What you must show</th>
            </tr>
          </thead>
          <tbody>
            {STAGES.map((s) => {
              const isActive = s.order === selected;
              return (
                <tr
                  key={s.order}
                  onClick={() => pickRound(s.order)}
                  className={`cursor-pointer border-b border-slate-200 align-top transition-colors dark:border-slate-700 ${
                    isActive
                      ? T.activeRow
                      : 'odd:bg-slate-50/60 hover:bg-slate-100 dark:odd:bg-slate-800/40 dark:hover:bg-slate-800'
                  }`}
                >
                  <td className="py-3 pr-3">
                    <span className={`font-bold ${isActive ? T.activeName : 'text-slate-800 dark:text-slate-100'}`}>
                      {s.round}
                    </span>
                  </td>
                  <td className="py-3 pr-3 text-slate-600 dark:text-slate-300">{wizardLabel(s)}</td>
                  <td className="py-3 pr-3 font-medium text-slate-800 dark:text-slate-100">
                    {range(s.raiseMin, s.raiseMax, s.raiseOpen)}
                  </td>
                  <td className="py-3 pr-3 font-medium text-slate-800 dark:text-slate-100">{stageTicket(s)}</td>
                  <td className="py-3 pr-3">
                    <ul className="flex flex-col gap-1">
                      {s.investors.map((inv) => (
                        <li key={inv.type} className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                          <span className="text-slate-700 dark:text-slate-200">{inv.type}</span>
                          <span className="rounded bg-slate-200/80 px-1.5 py-0.5 text-label-xs text-slate-500 dark:bg-slate-700 dark:text-slate-300">
                            {range(inv.ticketMin, inv.ticketMax, inv.open)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="py-3 text-slate-600 dark:text-slate-300">{s.maturityGate}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {!minimal && (
        <p className="mt-3 text-label-xs leading-relaxed text-slate-600 dark:text-slate-500">
          Pre-seed is the angel-led round — “angel” describes who writes the check, not a separate
          stage. Ticket sizes are per-investor; a round is typically filled by several of them. Ranges
          are indicative — starting points, not promises.
        </p>
      )}
    </section>
  );
};
